import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Lock, KeyRound } from "lucide-react";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const { login, token } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mustChange, setMustChange] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pendingToken, setPendingToken] = useState(null);

  const cls =
    "bg-black border-0 border-b border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-primary h-12 px-0 text-foreground";

  const routeForUser = (u) => (u?.is_admin ? "/admin" : "/portal");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const me = await login(email, password);
      if (me?.must_change_password) {
        setMustChange(true);
        setPendingToken(localStorage.getItem("ekg_token"));
        toast.info("Please choose a new password to continue.");
      } else {
        toast.success(`Welcome back, ${me?.name || "friend"}.`);
        navigate(routeForUser(me));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) return toast.error("Password must be at least 8 characters.");
    if (newPw !== confirmPw) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      const tk = pendingToken || token;
      await axios.post(
        `${API}/auth/change-password`,
        { new_password: newPw },
        { headers: { Authorization: `Bearer ${tk}` } },
      );
      const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${tk}` } });
      toast.success("Password updated.");
      navigate(routeForUser(me.data));
      // slight reload to refresh AuthContext user
      setTimeout(() => window.location.reload(), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password update failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        {!mustChange ? (
          <>
            <Lock className="text-secondary mb-6" size={28} />
            <p className="overline text-primary mb-3">Network Sign In</p>
            <h1 className="font-display uppercase text-5xl text-foreground mb-3">Sign In</h1>
            <p className="font-body text-sm text-muted-foreground mb-10">
              Admins, church leaders, staff, and members — one door.
            </p>
            <form onSubmit={submit} className="space-y-6" data-testid="admin-login-form">
              <Input
                data-testid="admin-email"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={cls}
                required
              />
              <Input
                data-testid="admin-password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={cls}
                required
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-submit"
                className="w-full bg-primary text-primary-foreground overline py-5 hover:bg-primary/85 transition-colors disabled:opacity-60"
              >
                {loading ? "Entering..." : "Enter"}
              </button>
            </form>
            <div className="mt-8 flex items-center justify-between text-sm">
              <span className="text-muted-foreground font-body">New to the Network?</span>
              <Link
                to="/signup"
                data-testid="link-signup"
                className="overline text-secondary hover:text-foreground"
              >
                Request Account →
              </Link>
            </div>
          </>
        ) : (
          <>
            <KeyRound className="text-secondary mb-6" size={28} />
            <p className="overline text-primary mb-3">First Login</p>
            <h1 className="font-display uppercase text-4xl text-foreground mb-3">Change Password</h1>
            <p className="font-body text-sm text-muted-foreground mb-10">
              Set a personal password to continue.
            </p>
            <form onSubmit={changePw} className="space-y-6" data-testid="force-password-form">
              <Input
                data-testid="new-password"
                type="password"
                placeholder="New password (min 8 chars)"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className={cls}
                required
              />
              <Input
                data-testid="confirm-password"
                type="password"
                placeholder="Confirm new password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className={cls}
                required
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="change-password-submit"
                className="w-full bg-primary text-primary-foreground overline py-5 hover:bg-primary/85 transition-colors disabled:opacity-60"
              >
                {loading ? "Saving..." : "Save & Continue"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
